import styled from 'styled-components';
import { Popup, PopupProps } from 'src/components/common/Popup';
import { Button } from 'src/components/common/Button';
import {
  cloneElement,
  ReactElement,
  useCallback,
  useRef,
  useState,
} from 'react';
import { AnimationControllerImperativeAPI } from 'src/components/common/AnimationController';
import { withAnimationController } from 'src/components/common/Button/withAnimationController';
import { useTranslate } from 'react-t7e';

export interface ConfirmationPopupProps extends PopupProps {
  title: string;
  description?: string;
  submitText?: string;
  onConfirm: () => void | Promise<void>;
  onCancel?: () => void;
}

const StyledPopup = styled(Popup)`
  background: transparent;
  display: flex;
  justify-content: center;
  align-items: center;

  width: auto;
  height: auto;
`;

const Form = styled.form`
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const Title = styled.h2`
  font-size: 24px;
  line-height: 1.3;
  font-weight: 300;
  text-transform: uppercase;
`;

const Description = styled.p`
  font-weight: 300;
  font-size: 12px;
  line-height: 18px;
  margin: 8px 0 0;
`;

const ActionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 40px;
`;

export const ConfirmationPopup = ({
  title,
  description,
  submitText,
  onConfirm,
  onCancel,
  ...props
}: ConfirmationPopupProps) => {
  const animationControllerRef =
    useRef<AnimationControllerImperativeAPI | null>(null);

  const { _ } = useTranslate();

  return (
    <StyledPopup {...props}>
      <Form
        onSubmit={withAnimationController(animationControllerRef, (e) => {
          e.preventDefault();
          return onConfirm();
        })}
      >
        <Title>{title}</Title>
        <Description>{description}</Description>
        <ActionsContainer>
          <Button
            type="submit"
            variant="secondary"
            stateControllerRef={animationControllerRef}
          >
            {submitText}
          </Button>
          <Button kind="text" onClick={onCancel} type="button">
            {_('Cancel')}
          </Button>
        </ActionsContainer>
      </Form>
    </StyledPopup>
  );
};

export const WithConfirmation = <
  Props extends { onClick?: (e: MouseEvent) => void },
>({
  children,
  onConfirm,
  onCancel,
  ...props
}: Partial<Props> &
  Omit<ConfirmationPopupProps, 'visible' | 'close'> & {
    children: ReactElement<Props>;
  }) => {
  const [popupVisible, setPopupVisible] = useState(false);

  const clickCallback = useCallback(
    (e: MouseEvent) => {
      children.props.onClick?.(e);
      setPopupVisible(true);
    },
    [children.props],
  );

  return (
    <>
      {cloneElement(children, {
        ...children.props,
        onClick: (e: MouseEvent) => clickCallback(e),
      })}
      <ConfirmationPopup
        visible={popupVisible}
        close={() => setPopupVisible(false)}
        onCancel={() => {
          onCancel?.();
          setPopupVisible(false);
        }}
        onConfirm={async () => {
          await onConfirm();
          setTimeout(() => {
            setPopupVisible(false);
          }, 300);
        }}
        {...props}
      />
    </>
  );
};
